<template>
	<div class="alert-wrapper sc-alert-md">
		<div class="alert-head sc-alert-md" v-if="header || subHeader">
			<h2 class="alert-title" v-if="header">{{ header }}</h2>
			<h3 class="alert-sub-title" v-if="subHeader">{{ subHeader }}</h3>
		</div>

		<div class="alert-message sc-alert-md" v-if="message">
			{{ message }}
		</div>

		<div class="alert-input-group sc-alert-md" v-if="!!inputsData.length">
			<div class="alert-input-wrapper sc-alert-md" v-for="(inputData, index) in inputsData" :key="index">
				<input
					class="alert-input sc-alert-md"
					:class="[inputData.cssClass]"
					v-bind="inputData"
					v-model="inputsData[index].value"
				/>
			</div>
		</div>

		<div class="alert-button-group sc-alert-md">
			<button
				v-for="(btn, index) in buttons"
				:key="index"
				class="alert-button sc-alert-md"
				:class="[btn.cssClass || `alert-message__button-${index}`]"
				@click="onClick(btn)"
			>
				<span class="alert-button-effect sc-alert-md">{{ btn.text }}</span>
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import { AlertButton, AlertInput } from '@/helpers/vfm/types';

const props = withDefaults(
	defineProps<{
		header: string;
		subHeader?: string;
		message?: string;
		buttons: AlertButton[];
		inputs?: AlertInput[];
	}>(),
	{
		subHeader: '',
		message: '',
		inputs: () => [],
		buttons: () => [],
	}
);

const inputsData = ref<AlertInput[]>(
	props.inputs.map(input => (input.name ? { ...input, value: input.value ?? '' } : input))
);

watch(
	() => props.inputs,
	newInputs => {
		inputsData.value = newInputs.map(input => (input.name ? { ...input, value: input.value ?? '' } : input));
	},
	{ deep: true }
);

function onClick(btn: AlertButton) {
	const inputValues = Object.fromEntries(
		inputsData.value
			.map(i => [i.name?.trim(), typeof i.value === 'string' ? i.value.trim() : i.value] as const)
			.filter(([k, v]) => !!k && v != null)
	);

	if (btn.handler) {
		Object.keys(inputValues).length > 0 ? btn.handler(inputValues) : btn.handler();
	}
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';

.alert-wrapper {
	display: flex;
	flex-direction: column;
	contain: content;
	z-index: 10;

	&.sc-alert-md {
		width: 280px;
	}

	.alert-head.sc-alert-md {
		padding-left: 23px;
		padding-right: 23px;
		padding-top: 20px;
		padding-bottom: 15px;
		text-align: start;

		.alert-title {
			font-size: 20px;
			font-weight: 500;
			color: white;
			margin: 0;
		}
	}

	.alert-message.sc-alert-md {
		padding: 0 24px 20px 24px;
		max-height: 266px;
		font-size: 16px;
		color: #737373;
	}

	.alert-input-group {
		padding: 20px 24px;

		.alert-input-wrapper {
			box-sizing: border-box;
			color: #737373;

			input {
				width: 100%;
				background-color: transparent;
				border: none;
				margin: 5px 0;
				padding: 10px 0;
				color: white;
				outline: none;
				border-bottom: 1px solid #d9d9d9;

				&:focus {
					border-bottom: 1px solid #fbc500;
				}
			}
		}
	}

	.alert-button-group.sc-alert-md {
		display: flex;
		justify-content: flex-end;
		padding: 8px;
		font-size: 14px;

		button {
			background: transparent;
			border: 0;
			color: white;
			padding: 10px;
			margin: 0 8px 0 0;
			cursor: pointer;
		}

		.alert-button {
			color: #5e6b76;

			&:last-child {
				color: #78a6b8;
			}
		}
	}
}
</style>
